import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ForumSeo } from '@/components/ForumSeo'
import { StaffRoleIcon } from '@/components/forum/StaffRoleIcon'
import { apiFetch } from '@/lib/api'
import {
  forumCard,
  forumLink,
  forumMutedSm,
  forumStatCell,
  forumStatGrid,
  forumKicker,
  forumTitleLg,
  forumMuted,
} from '@/lib/forumUi'
import { forumPageTitle, truncateForMetaDescription } from '~shared/seo'

export function ProfilePage() {
  const { username } = useParams<{ username: string }>()

  const [data, setData] = useState<{
    user: {
      username: string
      displayName: string
      avatarUrl: string | null
      role: string
      createdAt: number
      bio: string
      postCount: number
      threadCount: number
    }
    isSelf: boolean
  } | null>(null)

  useEffect(() => {
    if (!username) return
    let alive = true

    void apiFetch<{
      user: {
        username: string
        displayName: string
        avatarUrl: string | null
        role: string
        createdAt: number
        bio: string
        postCount: number
        threadCount: number
      }
      isSelf: boolean
    }>(`/api/users/${encodeURIComponent(username)}`)
      .then(payload => {
        if (!alive) return
        setData(payload)
      })
      .catch(() => {
        if (!alive) return
        setData(null)
      })

    return () => {
      alive = false
    }
  }, [username])

  if (!data) {
    return (
      <p className="rounded-xl border border-zinc-200 bg-white px-4 py-4 text-[14px] font-medium text-zinc-700 shadow-sm shadow-zinc-950/5">
        Member not found.
      </p>
    )
  }

  const initial = data.user.displayName.slice(0, 1).toUpperCase()
  const profileDescSource =
    data.user.bio.trim().length > 0 ?
      `${data.user.displayName}'s NK Our Town profile in North Kingstown RI: ${data.user.bio}`
    : `${data.user.displayName} (@${data.user.username}) participates in NK Our Town, a North Kingstown RI neighbor forum.`

  return (
    <article className={`${forumCard} md:grid md:grid-cols-[minmax(140px,200px)_1fr] md:gap-0 md:divide-x md:divide-zinc-100`}>
      <ForumSeo
        title={forumPageTitle(`@${data.user.username}`)}
        description={truncateForMetaDescription(profileDescSource)}
        noindex
      />
      <div className="p-5 md:border-b-0 md:border-r-0">
        <div className="aspect-square w-full overflow-hidden rounded-2xl border border-zinc-200 bg-gradient-to-br from-sky-50 to-zinc-50 md:mx-0 md:max-w-none">
          {data.user.avatarUrl ? (
            <img src={data.user.avatarUrl} alt={`${data.user.displayName} profile photo`} className="size-full object-cover" />
          ) : (
            <div aria-hidden className="grid size-full place-items-center text-4xl font-semibold tracking-tight text-zinc-600">
              {initial}
            </div>
          )}
        </div>
        <p className={`${forumMutedSm} mt-3 text-[12px] font-semibold uppercase tracking-wider text-zinc-500`}>
          Joined {new Date(data.user.createdAt * 1000).toLocaleDateString()}
        </p>
      </div>

      <div className="border-t border-zinc-100 p-5 md:border-t-0 md:p-6">
        <header>
          <p className={forumKicker}>Profile</p>
          <h1 className={`${forumTitleLg} mt-1`}>{data.user.displayName}</h1>
          <p className={`${forumMutedSm} mt-1 font-semibold text-zinc-800`}>@{data.user.username}</p>
        </header>

        <ul className={forumStatGrid} aria-label="Activity">
          <li className={`${forumStatCell} px-4 py-3`}>
            <p className="text-[11px] font-semibold uppercase tracking-wide text-zinc-500">Topics</p>
            <p className="mt-0.5 tabular-nums text-base font-semibold text-zinc-950">{data.user.threadCount}</p>
          </li>
          <li className={`${forumStatCell} px-4 py-3`}>
            <p className="text-[11px] font-semibold uppercase tracking-wide text-zinc-500">Posts</p>
            <p className="mt-0.5 tabular-nums text-base font-semibold text-zinc-950">{data.user.postCount}</p>
          </li>
          <li className={`${forumStatCell} px-4 py-3 min-w-[100%]`}>
            <p className="text-[11px] font-semibold uppercase tracking-wide text-zinc-500">Role</p>
            <p className="mt-0.5 inline-flex items-center gap-1.5 capitalize font-semibold text-zinc-950">
              <StaffRoleIcon role={data.user.role} />
              {data.user.role}
            </p>
          </li>
        </ul>

        <p className={`${forumMuted} mt-4`}>{data.user.bio || 'No bio yet.'}</p>

        {data.isSelf ? (
          <div className="mt-6">
            <Link className={`${forumLink} text-[14px]`} to="/settings">
              Account settings
            </Link>
          </div>
        ) : null}
      </div>
    </article>
  )
}
